import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ByIssueMetrics } from '../../types/service-insight-types';

/**
 * Displays the highest-impact service issues as selectable KPI cards.
 *
 * Each card represents an issue category and its associated call volume.
 * Users can select a single issue to apply an issue-based filter to the
 * parent @see ServiceDetailsTableComponent. When an issue is selected, the selected
 * card remains highlighted while all other cards are visually de-emphasized.
 *
 * Cards are ranked by call count and assigned Neptune severity colors from
 * darkest to lightest based on their relative position within the dataset.
 *
 * Responsibilities:
 * - Render issue metrics as compact KPI cards.
 * - Allow single-selection filtering by issue.
 * - Emit the selected issue name when a filter is applied.
 * - Emit an empty string when the active filter is cleared.
 * - Provide visual ranking through the Neptune color palette.
 */
@Component({
    selector: 'service-details-issues-filter',
    templateUrl: './service-details-issues-filter.component.html',
    styleUrls: ['./service-details-issues-filter.component.scss'],
    standalone: false
})
export class ServiceDetailsIssuesFilterComponent implements OnInit {
  @Input() data: ByIssueMetrics[] = [];

  @Output() issueFilterSelected: EventEmitter<string> =
    new EventEmitter<string>();

  totalItems: number = 0;
  sortedData: ByIssueMetrics[] = [];
  cardColor = 'bg-neptune-color-primary-700';
  displayLoader: boolean = false;
  issueSelected: string = '';

  /**
   * Card colors as defined in the Figma by UI/UX team.
   * The colors are ordered from darkest to lightest, representing the severity of the observation.
   */
  readonly neptuneCardColors = [
    'bg-neptune-color-primary-700', // darkest
    'bg-neptune-color-primary-600',
    'bg-neptune-color-primary-500',
    'bg-neptune-color-primary-400',
    'bg-neptune-color-primary-300', // lightest
  ];

  ngOnInit(): void {
    this.initializeIssueCards();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.displayLoader = true;
      this.data = changes['data'].currentValue;
      this.initializeIssueCards();
      this.issueFilterSelected.emit('');
      this.issueSelected = '';
      this.displayLoader = false;
    }
  }

  initializeIssueCards() {
    this.totalItems = this.data.length;
    this.sortedData = [...this.data].sort((a, b) => a.callCount - b.callCount);
  }

  onIssueSelectionChange(issueName: string, checked: boolean) {
    if (checked) {
      this.issueSelected = issueName;
      this.issueFilterSelected.emit(issueName);
    } else {
      this.issueSelected = '';
      this.issueFilterSelected.emit('');
    }
  }

  isIssueSelected(issueName: string): boolean {
    return this.issueSelected === issueName;
  }

  isCardDisabled(issueName: string): boolean {
    return this.issueSelected !== '' && this.issueSelected !== issueName;
  }

  /**
   * Returns the Neptune color class corresponding to an item's position
   * within the current issue list.
   *
   * The first issue receives the darkest color and the last issue receives
   * the lightest color. Intermediate issues are mapped proportionally
   * across the available color palette.
   */
  public getCardColor(index: number): string {
    if (this.totalItems <= 1) {
      return this.neptuneCardColors[0];
    }

    const colorIndex = Math.round(
      (index * (this.neptuneCardColors.length - 1)) / (this.totalItems - 1),
    );

    return this.neptuneCardColors[
      Math.min(colorIndex, this.neptuneCardColors.length - 1)
    ];
  }
}
